import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalErrorStore } from 'scbk-services';
import { Dialog } from 'scbk-shared-module';
import { useAutVrfAddGetDeivceAuth } from 'scbk-shared-module/modules/Additional/hooks/api/autVrfAdditionalHooks';
import { AutVrfAddGetDeviceAuthResponse } from 'scbk-shared-module/modules/Additional/types/api/autVrfAddGetDeviceAuth';
import {
  AccordionUnder,
  BulletList,
  BulletListItem,
  Empty,
  InfoListGroup,
  InfoListItem,
  SkeletonDefault,
} from 'scbk-ui-web';
import { useDeviceAuthStore } from '../stores/useDeviceAuthStore';

interface Props {
  trigger: number;
}

const DeviceAuthStep1Component: React.FC<Props> = ({ trigger }) => {
  const [deviceInfo, setDeviceInfo] = useState<AutVrfAddGetDeviceAuthResponse | undefined>();
  const [isSearch, setIsSearch] = useState<boolean>(false);

  // 다국어 처리
  const { t } = useTranslation();

  const { setMacAddress, setMaskMacAddress, setStep } = useDeviceAuthStore();

  /* 단말기지정서비스 조회 (S) */
  const { request: getDeviceInfo } = useAutVrfAddGetDeivceAuth({
    onError: (error) => {
      useGlobalErrorStore.getState().setErrorInfo({ ...error, title: `!!!!${error.code}` });
    },
  });

  const search = useCallback(async () => {
    const result: AutVrfAddGetDeviceAuthResponse | undefined = await getDeviceInfo();

    if (!result) return;

    setDeviceInfo(result);
    setMacAddress(result.macAddress);
    setMaskMacAddress(result.maskMacAddress);
    setIsSearch(true);

    console.log(`result, `, result);
  }, []);
  /* 단말기지정서비스 조회 (E) */

  const handleSubmit = async () => {
    if (deviceInfo?.deviceCount) {
      if (deviceInfo?.deviceCount >= 5) {
        Dialog.alert({
          content: [
            t('DeviceAuthComponent.032', '이미 5개의 단말기가 지정되어 있어요.'),
            t('DeviceAuthComponent.033', '지정해제 후 추가지정이 가능해요.'),
            t('DeviceAuthComponent.034', '다른 방법으로 추가인증을 진행해 주세요.'),
          ],
        });

        return;
      }
    }

    setStep(2);
  };

  useEffect(() => {
    if (trigger === 0) return;
    handleSubmit();
  }, [trigger]);

  const isMount = useRef(false);
  useEffect(() => {
    if (isMount.current) return;
    search();
    isMount.current = true;
  }, []);

  return (
    <>
      <h2 className="page-heading-title mb-8">
        {t('DeviceAuthComponent.005', '단말기 정보를 확인해 주세요')}
      </h2>

      {!isSearch && (
        <>
          <SkeletonDefault col={2} row={2} gap={10} />
          <SkeletonDefault col={3} row={2} gap={10} />
          <SkeletonDefault col={3} row={2} gap={10} />
        </>
      )}

      {isSearch && deviceInfo && deviceInfo?.deviceCount > 0 && (
        <div className="flex flex-col">
          {/* 260519 박스 타입 변경 */}
          <div className="common-border-box">
            <h3 className="heading-h5 font-weight-bold mb-5">
              {t('DeviceAuthComponent.006', '지정된 단말')}
            </h3>
            <InfoListGroup type="minimum">
              {deviceInfo.deviceAuthList.map((item) => (
                <InfoListItem
                  title={item.deviceAlias}
                  desc={item.macAddr}
                  className="first:mt-0 mt-4"
                />
              ))}
            </InfoListGroup>
          </div>
        </div>
      )}

      {isSearch && deviceInfo && deviceInfo?.deviceCount <= 0 && (
        <Empty text="등록된 단말기가 없어요" className="py-8" />
      )}

      {isSearch && (
        <>
          <div className="common-border-box mt-6">
            <h5 className="heading-h5 font-weight-bold mb-5">
              {t('DeviceAuthComponent.007', '단말기 지정 현황')}
            </h5>
            <InfoListGroup type="minimum">
              <InfoListItem
                title={t('DeviceAuthComponent.008', '최대 지정 가능한 기기 수')}
                desc="5"
              />
              <InfoListItem
                title={t('DeviceAuthComponent.009', '지정된 기기 수')}
                desc={(deviceInfo && deviceInfo?.deviceCount) ?? 0}
                className="first:mt-0 mt-4"
              />
              <InfoListItem
                title={t('DeviceAuthComponent.010', '추가 지정 가능한 기기 수')}
                desc={deviceInfo && deviceInfo?.deviceCount > 0 ? 5 - deviceInfo?.deviceCount : 5}
                className="first:mt-0 mt-4"
              />
            </InfoListGroup>
          </div>

          <AccordionUnder openTitle={t('DeviceAuthComponent.011', '알아두세요')}>
            <BulletList type="circle">
              <BulletListItem>
                {t(
                  'DeviceAuthComponent.012',
                  '전자금융사기 예방을 위해 제공되는 서비스로 최대 5대까지 단말기 지정을 할 수 있어요.',
                )}
              </BulletListItem>
              <BulletListItem>
                {t(
                  'DeviceAuthComponent.013',
                  '지정된 단말기에서는 자유롭게 인증서(재)발급, 타기관인증서 등록, 이체거래를 할 수 있어요.',
                )}
              </BulletListItem>
              <BulletListItem>
                {t('DeviceAuthComponent.014', '미지정 단말기에서는 조회 서비스만 가능해요.')}
              </BulletListItem>
              <BulletListItem>
                {t('DeviceAuthComponent.015', '미지정 단말기에서 이체거래가 필요한 경우')}
                <BulletList type="dash" size="smaller" className="mt-2">
                  <BulletListItem>
                    {t('DeviceAuthComponent.016', '추가로 단말기를 지정해 주세요.')}
                  </BulletListItem>
                  <BulletListItem>
                    {t(
                      'DeviceAuthComponent.017',
                      '이미 5개가 지정되어 있다면, 미지정단말기 사용을 허용하고 이용해 주세요.',
                    )}
                  </BulletListItem>
                  <BulletListItem>
                    {t(
                      'DeviceAuthComponent.018',
                      '인증서 발급/재발급, 타기관인증서 등록, 1일 누적 100만원 이상 이체할 때 추가인증 후 이체할 수 있어요.',
                    )}
                  </BulletListItem>
                </BulletList>
              </BulletListItem>
              <BulletListItem>
                {t(
                  'DeviceAuthComponent.019',
                  '네트워크(유/무선 LAN, WIFI, 3G, 블루투스 등) 사정에 따라 MAC 주소가 달라질 수 있으며, 수집이 안될 경우도 있어요.',
                )}
              </BulletListItem>
              <BulletListItem>
                {t('DeviceAuthComponent.020', '해외에서는 이용하는 경우')}
                <BulletList type="dash" size="smaller" className="mt-2">
                  <BulletListItem>
                    {t(
                      'DeviceAuthComponent.021',
                      '해외에서는 단말기 정보 수집이 어려워요. 해외 출국할 때 단말기 지정 해지 후 출국해 주세요.',
                    )}
                  </BulletListItem>
                  <BulletListItem>
                    {t(
                      'DeviceAuthComponent.022',
                      '휴대폰 추가인증을 할 수 없어요. 인증이 가능한 국내 본인 명의 연락처로 인증을 받아야 해요.',
                    )}
                  </BulletListItem>
                </BulletList>
              </BulletListItem>
              <BulletListItem>
                {t(
                  'DeviceAuthComponent.023',
                  '지정된 단말기 삭제는 모바일뱅킹 또는 영업점에서 할 수 있어요.',
                )}
              </BulletListItem>
            </BulletList>
          </AccordionUnder>
        </>
      )}
    </>
  );
};

export default DeviceAuthStep1Component;
